/**
 * Deciding that somebody is talking, and that they have stopped.
 *
 * Needed only when trunk drives the conversation itself: a hosted provider
 * does this far better and we leave it to them. It is the part of running
 * our own loop that we have to get right, so it is pure and tested rather
 * than buried in the hook.
 *
 * THREE IDEAS, each fixing something the previous one got wrong.
 *
 * LEVEL HYSTERESIS. Speech has to get loud to open a turn and stay quiet a
 * while to close one. A single threshold chatters: a voice crossing it on
 * every syllable starts and ends a dozen turns in a sentence.
 *
 * A LEARNED FLOOR, not a fixed number. An absolute threshold is a guess
 * about somebody else's microphone: too high and a softly-spoken person is
 * never heard, too low and a fan opens a turn. So it learns the room and
 * listens for speech above it.
 *
 * TIME HYSTERESIS, which is what stops a cough being a turn. A level has to
 * stay up for `onsetMs` before we believe it, because the moment a turn
 * opens we cut the agent off mid-sentence, and doing that for a keyboard
 * click is far worse than reacting 150ms later. The frames from before the
 * confirmation are kept by the caller as pre-roll, so nothing is clipped.
 */

export interface VadConfig {
  /**
   * How many times louder than the room somebody has to be to open a turn.
   * Relative, not absolute: see the note above.
   */
  openRatio: number
  /** Ratio to close at. Lower than `openRatio`, deliberately: hysteresis. */
  closeRatio: number
  /**
   * The bar to interrupt the agent WHILE IT IS TALKING, which is higher.
   *
   * Two reasons. Echo cancellation is imperfect, so the microphone hears
   * some of the speaker and would otherwise interrupt the agent with its
   * own voice. And the cost of being wrong is asymmetric: failing to hear
   * somebody is a short wait, cutting the agent off for a door closing
   * loses the answer they were listening to.
   */
  bargeInRatio: number
  /**
   * The quietest the room is ever believed to be.
   *
   * Without a floor, true digital silence gives a noise estimate of zero,
   * and any ratio of zero is zero, so the faintest sound opens a turn.
   */
  minFloor: number
  /** How long the level must stay up before it counts as speech. */
  onsetMs: number
  /**
   * The same wait, but WHILE THE AGENT IS TALKING, which is much longer.
   *
   * Echo is the problem this exists for. On a laptop with no headphones the
   * microphone hears the agent, echo cancellation leaves a residue, and the
   * residue is loud enough to clear even the higher barge-in ratio. What it
   * is not is SUSTAINED: it tracks the agent's own syllables and keeps
   * dropping between them, where a person cutting in holds the level.
   *
   * Local logs with the ratio alone still show utterances of 5.76s and
   * 6.66s transcribing to nothing, each one having cut a reply off first.
   * Nearly half a second of continuous speech over the top is a much
   * better test of intent, and it costs a real interrupter nothing they
   * would notice.
   */
  bargeInOnsetMs: number
  /** How long it has to stay quiet before the turn is over. */
  silenceMs: number
  /** Ignore a blip: a cough should not become an utterance. */
  minSpeechMs: number
  /** Stop a monologue running forever and timing the turn out. */
  maxUtteranceMs: number
}

export const DEFAULT_VAD: VadConfig = {
  openRatio: 3.5,
  closeRatio: 2,
  bargeInRatio: 6,
  minFloor: 0.002,
  // Long enough to reject a click or a cough, short enough that nobody
  // notices it before the agent stops talking.
  onsetMs: 180,
  bargeInOnsetMs: 450,
  silenceMs: 700,
  minSpeechMs: 250,
  maxUtteranceMs: 20_000,
}

export type VadEvent = 'speech-start' | 'speech-end' | null

export interface VadState {
  /**
   * `onset` is "something is loud but we do not believe it yet". Nothing
   * acts on it, which is the whole point.
   */
  phase: 'idle' | 'onset' | 'speaking'
  /** When the level first rose, so a confirmed turn dates from its real start. */
  onsetAt: number | null
  startedAt: number | null
  /** When it last dropped below the closing threshold. */
  quietSince: number | null
  /**
   * The running estimate of the room.
   *
   * Learned only while idle, and it falls fast and rises slowly: the
   * quietest thing heard recently is a far better estimate of a room than
   * an average, which speech drags upwards until the detector goes deaf to
   * the person talking.
   */
  floor: number
}

export const initialVad = (): VadState => ({
  phase: 'idle',
  onsetAt: null,
  startedAt: null,
  quietSince: null,
  floor: 0,
})

/** The thresholds in force, for display and for tests. */
export function thresholds(
  state: VadState,
  config: VadConfig = DEFAULT_VAD,
  agentSpeaking = false
) {
  const floor = Math.max(state.floor, config.minFloor)
  return {
    open: floor * (agentSpeaking ? config.bargeInRatio : config.openRatio),
    close: floor * config.closeRatio,
  }
}

/** Root mean square of a frame, 0..1. The cheapest honest loudness measure. */
export function rms(samples: Float32Array): number {
  if (samples.length === 0) return 0
  let sum = 0
  for (let i = 0; i < samples.length; i++) sum += samples[i] * samples[i]
  return Math.sqrt(sum / samples.length)
}

/**
 * Advance the detector by one frame. Pure: state in, state and event out.
 *
 * `now` is passed rather than read so behaviour is testable without waiting
 * in real time. `agentSpeaking` raises the bar to interrupt.
 */
export function step(
  state: VadState,
  level: number,
  now: number,
  config: VadConfig = DEFAULT_VAD,
  agentSpeaking = false
): { state: VadState; event: VadEvent } {
  const { open, close } = thresholds(state, config, agentSpeaking)

  if (state.phase === 'idle') {
    // Learn the room. Down quickly to follow a room going quiet, up slowly
    // so one loud moment does not deafen the detector for a minute.
    const floor =
      state.floor === 0
        ? level
        : level < state.floor
          ? state.floor * 0.7 + level * 0.3
          : state.floor * 0.98 + level * 0.02

    if (level >= open) {
      return {
        state: { ...state, phase: 'onset', onsetAt: now, floor },
        event: null,
      }
    }
    return { state: { ...state, floor }, event: null }
  }

  if (state.phase === 'onset') {
    if (level < close) {
      // It was a blip. Nothing was ever interrupted, which is the point.
      return { state: { ...initialVad(), floor: state.floor }, event: null }
    }
    const since = state.onsetAt ?? now
    // Talking over the agent has to be held for longer than starting from
    // silence does. See `bargeInOnsetMs`.
    const wait = agentSpeaking ? config.bargeInOnsetMs : config.onsetMs
    if (now - since < wait) return { state, event: null }
    // Believed. The turn dates from when the level first rose, so the
    // caller's pre-roll lines up with `startedAt`.
    return {
      state: {
        ...state,
        phase: 'speaking',
        startedAt: since,
        quietSince: null,
      },
      event: 'speech-start',
    }
  }

  // Speaking. A monologue that runs too long is cut so the turn can be
  // answered rather than timing out somewhere downstream.
  if (
    state.startedAt !== null &&
    now - state.startedAt >= config.maxUtteranceMs
  ) {
    return {
      state: { ...initialVad(), floor: state.floor },
      event: 'speech-end',
    }
  }

  if (level >= close) {
    return { state: { ...state, quietSince: null }, event: null }
  }

  const quietSince = state.quietSince ?? now
  if (now - quietSince < config.silenceMs) {
    return { state: { ...state, quietSince }, event: null }
  }

  const spoke = state.startedAt === null ? 0 : quietSince - state.startedAt
  return {
    state: { ...initialVad(), floor: state.floor },
    event: spoke >= config.minSpeechMs ? 'speech-end' : null,
  }
}
